import React from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ResultCard from "../../advanceSearch/ResultCard";
import { StaticViewConfigProvider } from "../../app/ViewConfigContext";
import TypeIcon from "../../app/typeIcons";
import { attributeLabel, displayValue, fieldsFor, setting, shortName, typeInfo, withDefaults } from "../../../utils/viewConfig";
import { brand } from "../../../theme/businessFlowsTheme";
import "../../customD3Tree/styles.css";

const SAMPLE_DESCRIPTION =
  "This is how objects of this type read on a search result card. Longer texts are shortened after the length set under Settings.";

const VISIBILITY = [
  ["inExplorer", "Explorer"],
  ["inSearch", "Search"],
  ["inQuickSearch", "Quick search"],
  ["inStructure", "Maps"],
];

// `inert` keeps the sample's buttons out of the tab order and away from the
// pointer. React 17 doesn't know the attribute, so it is set on the element.
const makeInert = (element) => {
  if (element) element.setAttribute("inert", "");
};

// An example object with the fields cards show for this type.
const sampleItem = (config, info) => {
  const configured = config.fields?.card?.[info.objectType] || [];
  const extras = Object.fromEntries(
    configured.filter((key) => key !== "Description").map((key) => [key, `Example ${key.replace(/_/g, " ").toLowerCase()}`])
  );
  return { GUID: "preview", name: `Example ${info.label}`, Type: info.objectType, Description: SAMPLE_DESCRIPTION, ...extras };
};

// How a type looks to users with the current settings: a result card and an
// explorer node, drawn by the same components users see.
// `item` is a real object to draw instead of an example one; `showDetails`
// adds the details panel; `showVisibility` can drop the four view chips,
// which say nothing where a view is a plain whitelist.
// `empty` is what to say when there is nothing to draw.
export default function CardPreview({ config, objectType, item: given, intro, showDetails = false, showVisibility = true, empty }) {
  const types = config?.types || [];
  if (!config || types.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: `1px solid ${brand.line}` }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: "text.secondary" }}>
          Preview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {empty || "Nothing to preview yet: this database has no object types."}
        </Typography>
      </Paper>
    );
  }

  const resolved = withDefaults(config);
  const info = typeInfo(resolved, types.some((t) => t.objectType === objectType) ? objectType : types[0].objectType);
  const item = given ? { ...given, Type: info.objectType } : sampleItem(resolved, info);
  const details = showDetails ? fieldsFor(resolved, "card", item) : [];

  return (
    <StaticViewConfigProvider config={config}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: `1px solid ${brand.line}` }}>
        <Typography variant="overline" sx={{ fontWeight: 700, color: "text.secondary" }}>
          Preview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {intro || `How ${info.label} looks to users. Choose a type to preview it.`}
        </Typography>

        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Search result
        </Typography>
        <Box ref={makeInert} aria-hidden sx={{ mt: 0.75, mb: 2, pointerEvents: "none" }}>
          <ResultCard item={item} path="" selected={false} starred={false} onSelect={() => {}} onToggleStar={() => {}} onOpenExplorer={() => {}} />
        </Box>

        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Relationship explorer
        </Typography>
        <Box ref={makeInert} aria-hidden sx={{ mt: 0.75, mb: 2, maxWidth: 260 }}>
          <div className="re-node" style={{ "--re-accent": info.colour, position: "relative" }}>
            <span className="re-node__text">
              <span className="re-node__type">
                <TypeIcon name={info.icon} sx={{ fontSize: 12, mr: 0.5, verticalAlign: "-2px" }} />
                {info.label}
              </span>
              <span className="re-node__name">{shortName(item.name, setting(resolved, "explorer.nodeLabelLength"))}</span>
            </span>
          </div>
        </Box>

        {showDetails && (
          <>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Details panel
            </Typography>
            <Box sx={{ mt: 0.75, mb: 2, border: `1px solid ${brand.line}`, borderRadius: 3, px: 1.5, py: 1.25 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", overflowWrap: "anywhere" }}>{item.name}</Typography>
              {details.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                  The name alone. Add details to this object type to show more.
                </Typography>
              ) : (
                details.map((key) => (
                  <Box key={key} sx={{ mt: 0.75 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block" }}>
                      {attributeLabel(key)}
                    </Typography>
                    <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                      {displayValue(item[key])}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          </>
        )}

        {showVisibility && (
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.75 }}>
          {VISIBILITY.map(([key, label]) => (
            <Chip
              key={key}
              size="small"
              icon={info[key] ? <CheckRoundedIcon /> : <CloseRoundedIcon />}
              label={label}
              variant={info[key] ? "filled" : "outlined"}
              sx={{ fontWeight: 600, bgcolor: info[key] ? "rgba(15,123,67,0.12)" : "transparent" }}
            />
          ))}
        </Stack>
        )}
      </Paper>
    </StaticViewConfigProvider>
  );
}
